import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useGet, usePost } from '../hooks';
import { useMovieStore } from '../stores';
import { Editor } from '../components/Editor/Editor';
import './EditorPage.css';
import { Logo } from '../components/Logo';
const normalizeDimension = (value) => (value > 0 ? value : 500);
export function EditorPage() {
    // Fetch all three movie lists from separate endpoints
    const { data: allMoviesData, loading: allLoading, error: allError } = useGet('/api/movies/getUserAllMovies');
    const { data: recentMoviesData, execute: refreshRecentMovies } = useGet('/api/movies/getUserRecentMovies');
    const { data: sampleMoviesData } = useGet('/api/movies/getSampleMovies');
    const { userAllMovies, userRecentMovies, setUserAllMovies, setUserRecentMovies, setSampleMovies, selectedMovieId, } = useMovieStore();
    const selectedMovieUrl = selectedMovieId ? `/api/movies/${selectedMovieId}` : null;
    const { data: selectedMovie } = useGet(selectedMovieUrl, { enabled: !!selectedMovieId });
    const trackRecentUrl = selectedMovieId ? `/api/movies/recent/${selectedMovieId}` : null;
    const { execute: trackRecentMovie } = usePost(trackRecentUrl);
    // Populate userAllMovies
    useEffect(() => {
        if (!allMoviesData)
            return;
        const allMovies = allMoviesData.map((movie) => ({
            id: movie.id,
            name: movie.name,
            description: movie.description ?? '',
            width: normalizeDimension(movie.width),
            height: normalizeDimension(movie.height),
            fps: movie.fps,
            sceneCount: movie.sceneCount ?? 0,
            sceneObjectCount: movie.sceneObjectCount ?? 0,
            layerCount: movie.layerCount ?? 0,
            isSample: false,
        }));
        setUserAllMovies(allMovies);
    }, [allMoviesData, setUserAllMovies]);
    // Populate userRecentMovies
    useEffect(() => {
        if (!recentMoviesData)
            return;
        const recentMovies = recentMoviesData.map((movie) => ({
            id: movie.id,
            movieId: movie.id,
            name: movie.name,
            description: movie.description ?? '',
            width: normalizeDimension(movie.width),
            height: normalizeDimension(movie.height),
            fps: movie.fps,
            sceneCount: movie.sceneCount ?? 0,
            sceneObjectCount: movie.sceneObjectCount ?? 0,
            layerCount: movie.layerCount ?? 0,
            isSample: false,
        }));
        setUserRecentMovies(recentMovies);
    }, [recentMoviesData, setUserRecentMovies]);
    // Populate sampleMovies
    useEffect(() => {
        if (!sampleMoviesData)
            return;
        const sampleMovies = sampleMoviesData.map((movie) => ({
            id: movie.id,
            name: movie.name,
            description: movie.description ?? '',
            width: normalizeDimension(movie.width),
            height: normalizeDimension(movie.height),
            fps: movie.fps,
            sceneCount: movie.sceneCount ?? 0,
            sceneObjectCount: movie.sceneObjectCount ?? 0,
            layerCount: movie.layerCount ?? 0,
            isSample: true,
        }));
        setSampleMovies(sampleMovies);
    }, [sampleMoviesData, setSampleMovies]);
    // Ensure selected movie is reflected in "My Projects" immediately.
    // This keeps UI in sync after opening/copying from sample without requiring reload.
    // Guard with selectedMovieId so unload/delete does not re-add a stale selectedMovie
    // while useGet still holds the previous response for one render.
    useEffect(() => {
        if (!selectedMovieId || !selectedMovie || selectedMovie.id !== selectedMovieId)
            return;
        const selectedSummary = {
            id: selectedMovie.id,
            name: selectedMovie.name,
            description: selectedMovie.description ?? '',
            width: normalizeDimension(selectedMovie.width),
            height: normalizeDimension(selectedMovie.height),
            fps: selectedMovie.fps,
            sceneCount: selectedMovie.sceneCount ?? 0,
            sceneObjectCount: selectedMovie.sceneObjectCount ?? 0,
            layerCount: selectedMovie.layerCount ?? 0,
            isSample: false,
        };
        const isSameSummary = (movie) => (movie.name ?? '') === selectedSummary.name &&
            (movie.description ?? '') === selectedSummary.description &&
            movie.width === selectedSummary.width &&
            movie.height === selectedSummary.height &&
            movie.fps === selectedSummary.fps &&
            (movie.sceneCount ?? 0) === (selectedSummary.sceneCount ?? 0) &&
            (movie.sceneObjectCount ?? 0) === (selectedSummary.sceneObjectCount ?? 0) &&
            (movie.layerCount ?? 0) === (selectedSummary.layerCount ?? 0) &&
            movie.isSample === selectedSummary.isSample;
        const existingMovie = userAllMovies.find((movie) => movie.id === selectedMovie.id);
        const existingRecent = userRecentMovies.find((movie) => (movie.movieId ?? movie.id) === selectedMovie.id);
        const allNeedsUpdate = !existingMovie || !isSameSummary(existingMovie);
        const recentNeedsUpdate = !!existingRecent && !isSameSummary(existingRecent);
        if (!allNeedsUpdate && !recentNeedsUpdate) {
            return;
        }
        if (allNeedsUpdate) {
            if (existingMovie) {
                setUserAllMovies(userAllMovies.map((movie) => movie.id === selectedMovie.id ? { ...movie, ...selectedSummary } : movie));
            }
            else {
                setUserAllMovies([selectedSummary, ...userAllMovies]);
            }
        }
        if (recentNeedsUpdate && existingRecent) {
            setUserRecentMovies(userRecentMovies.map((movie) => (movie.movieId ?? movie.id) === selectedMovie.id
                ? { ...movie, ...selectedSummary, movieId: selectedMovie.id }
                : movie));
        }
    }, [
        selectedMovie,
        selectedMovieId,
        userAllMovies,
        userRecentMovies,
        setUserAllMovies,
        setUserRecentMovies,
    ]);
    // Track selected movie as recent and refresh recent list.
    useEffect(() => {
        if (!selectedMovieId)
            return;
        let isActive = true;
        const trackAndRefresh = async () => {
            await trackRecentMovie();
            if (!isActive)
                return;
            await refreshRecentMovies();
        };
        void trackAndRefresh();
        return () => {
            isActive = false;
        };
    }, [selectedMovieId, trackRecentMovie, refreshRecentMovies]);
    // Loading state - only block on allMovies (required), others load in background
    if (allLoading) {
        return (_jsxs("div", { className: "editor-page-loading", children: [_jsx("div", { className: "loading-spinner" }), _jsx(Logo, {}), _jsx("span", { children: "Loading movies..." })] }));
    }
    // Error state
    if (allError) {
        return (_jsxs("div", { className: "editor-page-error", children: [_jsxs("span", { children: ["Failed to load movies: ", allError] }), _jsx("button", { onClick: () => window.location.reload(), children: "Retry" })] }));
    }
    // Pass selected movie to Editor
    return _jsx(Editor, { currentMovie: selectedMovie });
}
